import { AuthorStats, Slide } from './types.js';

export class SlideGenerator {
  generateAuthorSlides(stats: AuthorStats): Slide[] {
    const slides: Slide[] = [];

    // Welcome slide
    slides.push({
      title: `L'année de ${stats.name} en code`,
      emoji: '👋',
      content: `
        <div class="welcome">
          <h1>${stats.name}</h1>
          <p class="badge">${stats.badge}</p>
          <p class="subtitle">Votre GitHub Wrapped 2024-2025</p>
        </div>
      `
    });

    // Commits slide
    slides.push({
      title: 'Total des commits',
      emoji: '📊',
      content: `
        <div class="stat-large">
          <div class="number">${stats.commits.toLocaleString()}</div>
          <div class="label">commits cette année</div>
        </div>
      `
    });

    // Lines of code slide
    slides.push({
      title: 'Lignes de code',
      emoji: '💻',
      content: `
        <div class="stats-grid">
          <div class="stat">
            <div class="number green">${stats.linesAdded.toLocaleString()}</div>
            <div class="label">lignes ajoutées</div>
          </div>
          <div class="stat">
            <div class="number red">${stats.linesRemoved.toLocaleString()}</div>
            <div class="label">lignes supprimées</div>
          </div>
          <div class="stat">
            <div class="number">${Math.abs(stats.netLines).toLocaleString()}</div>
            <div class="label">net ${stats.netLines >= 0 ? 'ajoutées' : 'supprimées'}</div>
          </div>
        </div>
      `
    });

    // Activity patterns
    if (stats.mostActiveHour || stats.mostActiveDay) {
      slides.push({
        title: 'Votre rythme de code',
        emoji: '⏰',
        content: `
          <div class="stats-list">
            ${stats.mostActiveHour ? `
              <div class="stat-item">
                <div class="label">Heure de pointe</div>
                <div class="value">${stats.mostActiveHour}</div>
              </div>
            ` : ''}
            ${stats.mostActiveDay ? `
              <div class="stat-item">
                <div class="label">Jour préféré</div>
                <div class="value">${stats.mostActiveDay}</div>
              </div>
            ` : ''}
            <div class="stat-item">
              <div class="label">Jours actifs</div>
              <div class="value">${stats.activeDays}</div>
            </div>
            <div class="stat-item">
              <div class="label">Plus longue série</div>
              <div class="value">${stats.longestStreak} jours</div>
            </div>
          </div>
        `
      });
    }

    // PRs Stats slide (if available)
    if (stats.prs) {
      const mergeRate = stats.prs.opened > 0 
        ? Math.round((stats.prs.merged / stats.prs.opened) * 100) 
        : 0;
      slides.push({
        title: 'Pull Requests',
        emoji: '🔀',
        content: `
          <div class="stats-grid">
            <div class="stat">
              <div class="number">${stats.prs.opened}</div>
              <div class="label">PR ouvertes</div>
            </div>
            <div class="stat">
              <div class="number green">${stats.prs.merged}</div>
              <div class="label">PR fusionnées</div>
            </div>
            <div class="stat">
              <div class="number">${mergeRate}%</div>
              <div class="label">taux de fusion</div>
            </div>
          </div>
          ${stats.mostCommentedPR && stats.mostCommentedPR.comments > 0 ? `
            <div class="fun-fact">
              PR la plus discutée : #${stats.mostCommentedPR.prNumber} (${stats.mostCommentedPR.comments} commentaires)
            </div>
          ` : ''}
        `
      });
    }

    // Work habits slide
    const workHabits: string[] = [];
    if (stats.fridayWarriorCommits > 10) workHabits.push(`🎉 ${stats.fridayWarriorCommits} commits le vendredi`);
    if (stats.weekendWarriorCommits > 0) workHabits.push(`🏋️ ${stats.weekendWarriorCommits} commits le week-end`);
    if (stats.nightOwlCommits > 0) workHabits.push(`🦉 ${stats.nightOwlCommits} commits tard le soir`);
    if (stats.earlyBirdCommits > 0) workHabits.push(`🌅 ${stats.earlyBirdCommits} commits tôt le matin`);
    
    if (workHabits.length > 0) {
      slides.push({
        title: 'Habitudes de travail',
        emoji: '⏰',
        content: `
          <div class="habits-list">
            ${workHabits.map(h => `<div class="habit-item">${h}</div>`).join('')}
          </div>
          ${stats.avgCommitMessageLength > 0 ? `
            <div class="fun-stat">
              <span class="fun-label">Message de commit moyen :</span>
              <span class="fun-value">${Math.round(stats.avgCommitMessageLength)} caractères</span>
              <span class="fun-comment">${this.getMessageLengthComment(stats.avgCommitMessageLength)}</span>
            </div>
          ` : ''}
        `
      });
    }

    // Special achievements
    const achievements: string[] = [];
    if (stats.longestStreak > 4) achievements.push(`🔥 Série de ${stats.longestStreak} jours de code`);
    if (stats.prs && stats.prs.merged > 80) achievements.push(`✅ ${stats.prs.merged} PR fusionnées`);
    if (stats.activeDays > 100) achievements.push(`📅 ${stats.activeDays} jours actifs`);
    if (stats.linesAdded > 30000) achievements.push(`📝 ${(stats.linesAdded / 1000).toFixed(1)}K lignes ajoutées`);
    if (stats.commits > 200) achievements.push(`💪 ${stats.commits} commits`);

    if (achievements.length > 0) {
      slides.push({
        title: 'Réalisations',
        emoji: '🏆',
        content: `
          <div class="achievements">
            ${achievements.map(a => `<div class="achievement">${a}</div>`).join('')}
          </div>
        `
      });
    }

    // Top files
    if (stats.topFiles.length > 0) {
      slides.push({
        title: 'Fichiers les plus modifiés',
        emoji: '📁',
        content: `
          <div class="files-list">
            ${stats.topFiles.slice(0, 5).map(f => `
              <div class="file-item">
                <div class="file-name">${f.file}</div>
                <div class="file-commits">${f.commits} commits</div>
              </div>
            `).join('')}
          </div>
        `
      });
    }

    return slides;
  }

  generateTeamSlide(totalCommits: number, totalLines: number): Slide {
    return {
      title: 'Statistiques de l\'équipe',
      emoji: '🎉',
      content: `
        <div class="stat-large">
          <div class="number">${totalCommits.toLocaleString()}</div>
          <div class="label">commits totaux de l'équipe</div>
          <div class="number">${totalLines.toLocaleString()}</div>
          <div class="label">lignes totales modifiées</div>
        </div>
      `
    };
  }

  private getMessageLengthComment(length: number): string {
    if (length < 30) return '(court et doux)';
    if (length < 50) return '(concis)';
    if (length < 70) return '(descriptif)';
    return '(conteur)';
  }
}
